import React, { useState, useEffect } from "react";
import { EditorRow, SortableRow } from "src/components/content-builder";
import { v4 as uuidv4 } from "uuid";
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Stack, Dialog, DialogTitle, DialogContent, Button, TextField } from "@mui/material";
import { useNotify } from "react-admin";

export const EditorBuilder = (props) => {
  const { onEditorChange, value } = props
  const notify = useNotify();

  const [importOpen, setImportOpen] = useState(false);
  const [importJsonData, setImportJsonData] = useState(null);

  const handleImportOpen = () => {
    setImportJsonData(null);
    setImportOpen(true);
  };

  const handleImportClose = () => {
    setImportOpen(false);
    setImportJsonData(null);
  };

  const newRow = {
    id: uuidv4(),
    grid: [12],
    columns: [{ id: uuidv4(), component: null }]
  };

  const [rows, setRows] = useState([newRow]);

  useEffect(() => {
    if (isValidJSON(value)) {
      let parsedData = JSON.parse(value);

      if (isValidData(parsedData)) {
        setRows(parsedData);
      }
    }
  }, [value]);

  const addRow = () => {
    updateRows([...rows, newRow]);
  };

  const removeRow = (row) => {
    updateRows(rows.filter(r => (r.id !== row.id)));
  };

  const updateRow = (id, updatedRow) => {
    updateRows(rows.map(r => (r.id === id ? updatedRow : r)));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = rows.findIndex(r => r.id === active.id);
      const newIndex = rows.findIndex(r => r.id === over.id);
      updateRows(arrayMove(rows, oldIndex, newIndex));
    }
  };

  const updateRows = (rows) => {
    setRows(rows);
    onEditorChange(JSON.stringify(rows, null, 2));
  };

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(rows, null, 2));
    notify(`JSON has been copied to the clipboard.`, { type: 'success' });
  };

  const importJson = () => {
    if (isValidJSON(importJsonData)) {
      let parsedData = JSON.parse(importJsonData);

      if (isValidData(parsedData)) {
        updateRows(parsedData);
        handleImportClose();
        notify(`JSON has been imported successfully.`, { type: 'success' });
      } else {
        notify(`JSON data is not compatible with editor`, { type: 'error' });
      }
    } else {
      notify(`Wrong format, must be JSON format.`, { type: 'error' });
    }
  };

  return (
    <Box className="editor-builder">
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
          {rows.map((row) => (
            <SortableRow key={row.id} id={row.id}>
              <EditorRow
                row={row}
                onUpdate={(updatedRow) => updateRow(row.id, updatedRow)}
                onRemove={removeRow}
              />
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>

      <Stack direction="row" spacing={2} className="editor-builder-controls">
        <Box display="flex" justifyContent="flex-start" gap={2}>
          <span onClick={addRow} className="editor-button add-row">
            + Add Row
          </span>
        </Box>
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <span onClick={copyJson} className="editor-button copy-json">
            Copy JSON
          </span>
          <span onClick={handleImportOpen} className="editor-button import-json">
            Import JSON
          </span>
        </Box>
      </Stack>

      <Dialog open={importOpen} onClose={handleImportClose} fullWidth maxWidth="sm">
        <DialogTitle>Import JSON</DialogTitle>
        <DialogContent>
          <TextField
            label="JSON"
            size="small"
            multiline
            rows={8}
            onChange={(e) =>
              setImportJsonData(e.target.value)
            }
          ></TextField>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="contained" color="primary" onClick={() => importJson()}>Import</Button>
            <Button onClick={handleImportClose}>Cancel</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};


export const isValidJSON = (data: string) => {
  try {
    JSON.parse(data);
    return true;
  } catch (e) {
    return false;
  }
}

export const isValidData = (data: any): boolean => {
  if (!Array.isArray(data)) return false;

  try {
    return data.every(row => {
      return (
        typeof row === "object" &&
        row.id &&
        Array.isArray(row.grid) &&
        Array.isArray(row.columns) &&
        row.columns.every(col => {
          return (
            typeof col === "object" &&
            col.id &&
            typeof col.component === "object" &&
            typeof col.component.id === "string" &&
            typeof col.component.name === "string" &&
            Array.isArray(col.component.fields) &&
            col.component.fields.every(f =>
              typeof f.name === "string" &&
              typeof f.label === "string"
            ) &&
            typeof col.component.data === "object"
          );
        })
      );
    });
  } catch (e) {
    return false;
  }
};